import { Tooltip } from "antd";

import { pipe, Either } from "fp";

import React from "react";
import styled from "styled-components";

import { useAction } from "~/Action";
import * as Color from "~/Color";
import * as Data from "~/Data";
import * as ErrorMessage from "~/ErrorMessage";

import {
  CheckCircle,
  ExclamationCircle,
  ExclamationTriangle,
  Pending,
  PaperPlane,
  TimesCircle,
} from "~/Icon";

import * as Layout from "~/Layout";
import { Order } from "~/Order";
import * as Typography from "~/Typography";
import { WebAPI } from "~/WebAPI";

import * as Action from "./Action";

export * as Action from "./Action";
export * as Request from "./Request";

export type Status = Either<Failure.Failure, Success.Success>;

type Props = Data.Reference.Reference & { readonly status: Status };

export const success = () => "Success" as const;
export const failure = () => "Failure" as const;
export const pending = () => "Pending" as const;
export const warning = () => "Warning" as const;

export namespace Success {
  export type Kind = ReturnType<
    typeof success | typeof pending | typeof warning
  >;

  /**
   * A submission that has not failed. `message` carries the API's
   * `statusMessage` for a `Warning` — the non-fatal remark FMCSA returned
   * alongside an accepted file — and is absent otherwise.
   */
  export type Success = {
    readonly kind: Kind;
    readonly message?: string;
  };

  export const of = (kind: Kind, message?: string): Success => ({
    kind,
    message,
  });
}

export const isResendRequesting = (status: Status): boolean =>
  pipe(
    status,
    Either.map(() => false),
    Either.getOrElse(({ resend }) =>
      Data.Remote.when(resend, {
        idle: () => false,
        loading: () => true,
        error: () => false,
        success: () => false,
      })
    )
  );

export const failureFromStatus = (
  status: Status
): Failure.Failure | undefined =>
  pipe(
    status,
    Either.fold(
      (failure) => failure,
      () => undefined
    )
  );

export const toString = (status: Status): string =>
  pipe(
    status,
    Either.map(({ kind }) => kind),
    Either.getOrElseW(() => failure())
  );

/** The non-fatal remark on a `Warning`, if the API sent one. */
const messageFromSuccess = (status: Status): string | undefined =>
  pipe(
    status,
    Either.fold(
      () => undefined,
      ({ message }) => message
    )
  );

type TextProps = { readonly small?: boolean };

const ErrorText = styled.span<TextProps>`
  color: ${ErrorMessage.Danger.color()};
  ${({ small }) =>
    small ? `font-size: 0.8em; white-space: break-spaces;` : null}
`;

export namespace Failure {
  /**
   * Where in the pipeline the submission stopped. The three reported stages run
   * in this order, and the API reports the first one that failed, so the stage
   * says how far the file got: a `generation` failure produced no file at all,
   * `validation` built one that was never sent, and only `transmission` reached
   * FMCSA.
   *
   * `unrecognized` is not one of them. It is a status this build cannot read at
   * all, and it says nothing about how far the file got — which is the whole
   * point of keeping it separate rather than filing it under `transmission`.
   */
  export type Stage = ReturnType<
    | typeof generation
    | typeof validation
    | typeof transmission
    | typeof unrecognized
  >;

  export const generation = () => "generation" as const;
  export const validation = () => "validation" as const;
  export const transmission = () => "transmission" as const;
  export const unrecognized = () => "unrecognized" as const;

  export type Failure = {
    readonly stage: Stage;
    readonly message?: string;
    readonly resend: Data.Remote<Resend.Resend>;
  };

  /**
   * Why the submission failed, written for the person who submitted it. Kept
   * distinct per stage: telling someone their file was "not received by FMCSA"
   * when it was never built sends them looking in the wrong place.
   */
  export const toExplanation = (stage: Stage): string => {
    switch (stage) {
      case generation():
        return "ELD output file could not be generated, so nothing was sent to FMCSA. Contact Support for additional details.";
      case validation():
        return "ELD output file failed validation and was not sent to FMCSA. Contact Support for additional details.";
      case transmission():
        return "ELD not received by FMCSA. Contact Support for additional details.";
      case unrecognized():
        return "This submission reported a status this page does not recognize, so whether FMCSA received it is unknown. Contact Support for additional details.";
    }
  };

  export const resend = ({ resend, ...failure }: Failure): Failure => ({
    ...failure,
    resend: pipe(
      resend,
      Data.Remote.map(() => resend),
      Data.Remote.getOrElse(() => Data.Remote.loading())
    ),
  });

  export namespace Resend {
    export type Resend = Data.Reference.Reference;

    export type Props = {
      readonly id: Data.Reference.Reference;
      readonly failure: Failure;
    };

    export const { equals, compare } = Order.fromCompare<Status>((a, b) =>
      pipe([isSuccess(a), isSuccess(b)], ([isSuccessA, isSuccessB]) =>
        !isSuccessA && isSuccessB
          ? Order.lessThan()
          : !isSuccessB && isSuccessA
          ? Order.greaterThan()
          : Order.equal()
      )
    );

    // TODO: Move to `~/Data.Remote`
    const isInProgress = (failure: Failure): boolean =>
      pipe(
        failure.resend,
        Data.Remote.when({
          idle: () => false,
          loading: () => true,
          error: () => false,
          success: () => false,
        })
      );

    // TODO: Move to `~/Data.Remote`
    const isComplete = (failure: Failure): boolean =>
      pipe(
        failure.resend,
        Data.Remote.when({
          idle: () => false,
          loading: () => false,
          error: () => true,
          success: () => true,
        })
      );

    // TODO: Move to `~/Data.Remote`
    const isError = (failure: Failure): boolean =>
      pipe(
        failure.resend,
        Data.Remote.when({
          idle: () => false,
          loading: () => false,
          error: () => true,
          success: () => false,
        })
      );

    const getResendError = (failure: Failure): string | undefined =>
      pipe(
        failure.resend,
        Data.Remote.when({
          loading: () => undefined,
          success: () => undefined,
          error: (error) => error.message,
        })
      );

    export const Component: React.Fn<Props> = ({ id, failure }) => {
      return (
        <Layout.Inline.Component>
          <button onClick={useAction(Action.Requested.create(id))}>
            {isComplete(failure) ? (
              isError(failure) ? (
                <>
                  <PaperPlane color={Color.steel()} />
                  <ErrorText color={Color.steel()}>Resend</ErrorText>
                  <Layout.Inline.Component left right>
                    <Tooltip
                      title={getResendError(failure)}
                      color={Color.black()}
                    >
                      <ExclamationCircle color={Color.guardsmanRed()} />
                    </Tooltip>
                  </Layout.Inline.Component>
                </>
              ) : (
                <>
                  <PaperPlane color={Color.azure()} />
                  <span>Resend</span>
                </>
              )
            ) : isInProgress(failure) ? (
              <>
                <PaperPlane color={Color.green()} />
                <Layout.Inline.Component all>
                  Resending...
                </Layout.Inline.Component>
              </>
            ) : (
              <>
                <PaperPlane color={Color.azure()} />
                <Layout.Inline.Component all>Resend</Layout.Inline.Component>
              </>
            )}
          </button>
        </Layout.Inline.Component>
      );
    };
  }
}

export const isSuccess = (status: Status): boolean => Either.isRight(status);
export const isPending = (status: Status): boolean =>
  toString(status) === pending();
export const isWarning = (status: Status): boolean =>
  toString(status) === warning();

export const { equals, compare } = Order.fromCompare<Status>((a, b) =>
  pipe([isSuccess(a), isSuccess(b)], ([isSuccessA, isSuccessB]) =>
    !isSuccessA && isSuccessB
      ? Order.lessThan()
      : !isSuccessB && isSuccessA
      ? Order.greaterThan()
      : Order.equal()
  )
);

export const Style = styled.span<Props>`
  & > span {
    cursor: default;
  }
`;

export const Component: React.Fn<Props> = ({ id, status }) => {
  const failure: Failure.Failure | undefined = failureFromStatus(status);
  if (failure) {
    return <FailureComponent status={status} failure={failure} />;
  }

  return <SuccessComponent status={status} id={id} />;
};

type FailureComponentProps = {
  readonly status: Status;
  readonly failure: Failure.Failure;
};

const FailureComponent: React.Fn<FailureComponentProps> = ({
  status,
  failure,
}) => {
  return (
    <Layout.Flex.Component row justify="space-between">
      <Layout.Flex.Component justify="center" right>
        <Typography.Component strong>
          <Layout.Inline.Component right>
            <TimesCircle color={Color.guardsmanRed()} />
          </Layout.Inline.Component>
          <Tooltip title={Failure.toExplanation(failure.stage)}>
            <ErrorText data-testid="failure-text">
              {toString(status)}!
            </ErrorText>
          </Tooltip>
        </Typography.Component>

        {failure.message ? (
          <ErrorText small>{failure?.message}</ErrorText>
        ) : null}
      </Layout.Flex.Component>
    </Layout.Flex.Component>
  );
};

const SuccessComponent: React.Fn<Props> = ({ status, id }) => {
  const icon = isPending(status) ? (
    <Pending color={Color.mustard()} data-testid="pending-icon" />
  ) : isWarning(status) ? (
    <ExclamationTriangle color={Color.mustard()} data-testid="warning-icon" />
  ) : (
    <CheckCircle color={Color.green()} data-testid="success-icon" />
  );

  return (
    <Style status={status} id={id}>
      <Tooltip
        title={
          isWarning(status)
            ? // The API's own remark when it sent one — it names the data issue,
              // where the generic line only says there was one.
              messageFromSuccess(status) ??
              "ELD received by FMCSA with data issue(s). Contact Support for additional details."
            : ""
        }
      >
        <Layout.Inline.Component right>
          <Layout.Inline.Component right>{icon}</Layout.Inline.Component>
          <>{toString(status)}</>
        </Layout.Inline.Component>
      </Tooltip>
    </Style>
  );
};

/**
 * Map the API's flat status onto this module's `Either`.
 *
 * The five values do not partition three ways on their own — `Warning` has no
 * enum value of its own. The API collapses what the legacy stack called
 * `WARNING` into `TRANSMISSION_SUCCEEDED` and keeps the remark in
 * `statusMessage`, so a succeeded submission carrying one is a warning and a
 * succeeded submission without one is a plain success.
 */
export const fromWebAPI = ({
  status,
  statusMessage,
}: Pick<WebAPI.ELD.SubmissionFragment, "status" | "statusMessage">): Status => {
  const message = statusMessage ?? undefined;

  switch (status) {
    case "PENDING":
      return Either.right(Success.of(pending()));
    case "TRANSMISSION_SUCCEEDED":
      return message
        ? Either.right(Success.of(warning(), message))
        : Either.right(Success.of(success()));
    case "GENERATION_FAILED":
      return Either.left({
        stage: Failure.generation(),
        message,
        resend: Data.Remote.idle(),
      });
    case "VALIDATION_FAILED":
      return Either.left({
        stage: Failure.validation(),
        message,
        resend: Data.Remote.idle(),
      });
    case "TRANSMISSION_FAILED":
      return Either.left({
        stage: Failure.transmission(),
        message,
        resend: Data.Remote.idle(),
      });
    default:
      // `status` is typed as these five values, but nothing decodes it at the
      // boundary — the WebAPI types are written against the SDL rather than
      // generated from it. Without this the switch falls off the end and hands
      // back `undefined` where the signature promises a `Status`, and the row
      // breaks instead of the status being unreadable.
      return Either.left({
        stage: Failure.unrecognized(),
        message: `Unrecognized submission status: ${String(status)}`,
        resend: Data.Remote.idle(),
      });
  }
};
